import { Store } from '@ngrx/store';
import { delay, filter, first, interval, of, switchMap } from 'rxjs';
import { inject } from '@angular/core';
import { map, tap } from 'rxjs/operators';
import { AppStore } from '@aaa/emember/store-types';
import { isMembershipInitialized, MembershipActions } from '@aaa/emember/store-membership';
import { CookiesService } from '../share/services/cookies.service';
import { AvaDialogService } from '../share/services/ava-dialog.service';
import { ClubApp } from '@aaa/emember/types';
import { ZipcodeFormComponent } from './components/zipcode-form/zipcode-form.component';
import { isSessionInitialized, SessionActions } from '@aaa/emember/store-session';
import { environment } from '../../../environments/environment';
import { GoogleTagManagerService } from '../share/services/google-tag-manager.service';
import { LocationRefService } from '../share/services/location-ref.service';

const MODAL_CLASS_SMALL = 'ava-dialog-small';
const DELAY_TIME = 10;
const CHECK_INTERVAL = 500;

export function membershipInitializing() {
  const dialog = inject(AvaDialogService);
  const store = inject(Store<AppStore>);
  const location = inject(LocationRefService);
  const cookies = inject(CookiesService);
  const googleTagManager = inject(GoogleTagManagerService);

  function initializeGTM() {
    googleTagManager.init(environment.googleTagManager);
  }

  function updateZipcodeFromCookie(clubId: ClubApp) {
    return of(true).pipe(
      map(() => cookies.getZipInfo()),
      filter((info) => !!info.zipcode && info.clubId === clubId),
      tap(({ zipcode }) => store.dispatch(MembershipActions.setZipcode({ zipcode }))),
      delay(10),
      map(() => true),
    );
  }

  function isZipcodeValid(zipcode: string) {
    return !!zipcode;
  }

  function waitUntilMembershipReady() {
    store.dispatch(MembershipActions.initialize({ env: environment }));

    return store.select(isMembershipInitialized).pipe(first((status) => status));
  }

  function waitUntilSessionReady() {
    store.dispatch(SessionActions.initialize());

    return store.select(isSessionInitialized).pipe(first((status) => status));
  }

  function fetchSearchParams() {
    return location
      .getSearchParam('rclub', ['rClub', 'RCLUB'])
      .pipe(map((rClub) => ({ clubId: environment.clubId, rClub })));
  }

  function openPopup() {
    const modal = dialog.openComponent({
      nzContent: ZipcodeFormComponent,
      nzFooter: null,
      nzClosable: false,
      nzMaskClosable: false,
      nzKeyboard: false,
      nzClassName: MODAL_CLASS_SMALL,
    });
    return modal;
  }

  function ensureZipcodeState(clubId: ClubApp, rClub: string) {
    // Update cookies and state with zipcode from query params
    const zipInfo = cookies.getZipInfo();
    // stateZipcode   |   cookieZipcode
    // --------------------------------
    // +					          -			updateCookie
    // +					          +			updateState
    // -					          +			updateState
    // -					          -			popup

    // set state zipcode from cookies
    if (isZipcodeValid(zipInfo.zipcode) && zipInfo.clubId === clubId) {
      console.log('updateState ', zipInfo.zipcode);
      return of(zipInfo.zipcode).pipe(
        tap((zipcode) => store.dispatch(MembershipActions.setZipcode({ zipcode }))),
        delay(DELAY_TIME),
        map(() => true),
      );
    }

    // check additional rClub parameter
    if (!isZipcodeValid(zipInfo.zipcode) && rClub === clubId) {
      return of(true).pipe(
        tap(() => cookies.setZipCode(environment.zipcode, rClub)),
        tap(() => store.dispatch(MembershipActions.setZipcode({ zipcode: environment.zipcode }))),
        delay(DELAY_TIME),
      );
    }

    // # Rollback changes WSSV3-1143
    if ([ClubApp.Northway].includes(clubId)) {
      const modal = openPopup();
      return interval(CHECK_INTERVAL).pipe(
        switchMap(() => updateZipcodeFromCookie(clubId)),
        first(),
        tap(() => modal.close()),
      );
    }

    console.log('wait for cookies zipcode');
    return interval(CHECK_INTERVAL).pipe(
      switchMap(() => updateZipcodeFromCookie(clubId)),
      first(),
    );
  }

  return () =>
    of(true).pipe(
      // Add tag manager
      tap(initializeGTM),
      // Wait until membership initialized
      switchMap(waitUntilMembershipReady),
      // Wait until session initialized
      switchMap(waitUntilSessionReady),
      // Get search params
      switchMap(fetchSearchParams),
      // Checking zip code
      switchMap(({ clubId, rClub }) => ensureZipcodeState(clubId, rClub)),
    );
}
